// eslint-disable-next-line jira/restricted/react-component-props
import React, { type ComponentType, type ComponentProps } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { AsyncProfileCardNext } from '@atlassian/jira-profilecard-next/src/async';
import { VIEW_PROFILE_ACTION } from '@atlassian/jira-profilecard-next/src/common/constants.tsx';
import { TenantContextSubscriber as TenantContextSubscriberDI } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { Link as LinkDI } from '@atlassian/react-resource-router';

type Agent = {
	accountId: string;
	displayName: string;
	avatarUrl: string;
};

type Props = {
	agent: Agent;
	Link: ComponentType<JSX.LibraryManagedAttributes<typeof LinkDI, ComponentProps<typeof LinkDI>>>;
	TenantContextSubscriber?: ComponentType<
		JSX.LibraryManagedAttributes<
			typeof TenantContextSubscriberDI,
			ComponentProps<typeof TenantContextSubscriberDI>
		>
	>;
};

const AgentCell = ({ agent, TenantContextSubscriber = TenantContextSubscriberDI, Link }: Props) => {
	const baseUrl = '';

	const { avatarUrl, displayName, accountId } = agent;

	if (accountId) {
		return (
			<AsyncProfileCardNext
				accountId={agent.accountId}
				TenantContextSubscriber={TenantContextSubscriber}
				actions={[VIEW_PROFILE_ACTION]}
			>
				<Wrapper>
					<StyledAgent>
						<Avatar src={avatarUrl} size="small" borderColor="transparent" />
						<Link href={`${baseUrl}/jira/people/${accountId}`}>
							<DisplayName>{displayName}</DisplayName>
						</Link>
					</StyledAgent>
				</Wrapper>
			</AsyncProfileCardNext>
		);
	}
	return null;
};

AgentCell.defaultProps = {
	Link: LinkDI,
};

export default AgentCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	padding: `${token('space.050', '4px')} 0`,
	display: 'flex',
	textAlign: 'left',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledAgent = styled.div({
	display: 'flex',
	flex: 1,
	alignItems: 'center',
	backgroundColor: 'transparent',
	border: 0,
	borderRadius: '3px',
	padding: `0 ${token('space.050', '4px')}`,
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 4}px`,
	overflowX: 'hidden',
	/* CSS reset to Atlaskit defaults */
	color: 'inherit',
	font: 'font.body',
	fontStyle: 'normal',
	outline: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DisplayName = styled.span({
	flex: 1,
	lineHeight: 1.4,
	paddingLeft: token('space.100', '8px'),
	marginRight: token('space.100', '8px'),
	overflowX: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	textAlign: 'left',
});
